import {
  HHBox,
  HHStack,
  HHTooltip,
  HHTypography,
} from '@hinge-health/react-component-library';
import { useMediaQuery } from '@mui/material';
import { FILE_PARSING_BATCH_STATUS_LABELS } from '../constants/partner-data-gateway.constant';
import { FileParsingBatchStatus } from '../domain/enums';
import { BatchHeatmapCell } from '../domain/types';
import { sortBatchesByByteOffset } from '../utils/batch.utils';
import { formatByteRange, formatCount } from '../utils/format.utils';

export const BATCH_HEATMAP_TEST_ID = 'pdg-batch-heatmap';
export const BATCH_HEATMAP_EMPTY_TEST_ID = 'pdg-batch-heatmap-empty';
export const BATCH_HEATMAP_CELL_TEST_ID_PREFIX = 'pdg-batch-heatmap-cell';

export const batchHeatmapCellTestId = (batchId: string): string =>
  `${BATCH_HEATMAP_CELL_TEST_ID_PREFIX}-${batchId}`;

/** ~24px keeps a full 100-batch page visible without scrolling. */
const CELL_SIZE_PX = 24;

/** Exported so tests assert against the real keyframe name, not a copy. */
export const PULSE_KEYFRAMES = 'pdg-batch-cell-pulse';

/**
 * Fill colour per batch status, as theme palette paths.
 *
 * Deliberately not the same palette as `StatusChip`: the chip labels its
 * status in text, whereas a cell has nothing but its colour, so `PENDING`
 * and `SKIPPED` need two distinguishable greys rather than one shared
 * "default".
 */
export const BATCH_CELL_COLORS: Record<FileParsingBatchStatus, string> = {
  [FileParsingBatchStatus.Pending]: 'grey.400',
  [FileParsingBatchStatus.Processing]: 'info.main',
  [FileParsingBatchStatus.Completed]: 'success.main',
  [FileParsingBatchStatus.Failed]: 'error.main',
  [FileParsingBatchStatus.Skipped]: 'grey.200',
};

/**
 * Whether a cell should run the PROCESSING pulse. Split out from the `sx`
 * ternary so the reduced-motion gating (the pause/stop/hide control for the
 * otherwise-indefinite animation) is unit-testable on its own.
 */
export const shouldPulseCell = (
  status: FileParsingBatchStatus,
  prefersReducedMotion: boolean,
): boolean =>
  status === FileParsingBatchStatus.Processing && !prefersReducedMotion;

const TooltipLine = ({ children }: { children: string }): JSX.Element => (
  <HHTypography hhVariant="variant-bypass" variant="caption" component="span">
    {children}
  </HHTypography>
);

const BatchTooltip = ({ batch }: { batch: BatchHeatmapCell }): JSX.Element => (
  <HHStack gap={0.25}>
    <TooltipLine>{`Batch ${batch.batchId}`}</TooltipLine>
    <TooltipLine>{FILE_PARSING_BATCH_STATUS_LABELS[batch.status]}</TooltipLine>
    <TooltipLine>
      {formatByteRange(batch.byteOffsetStart, batch.byteOffsetEnd)}
    </TooltipLine>
    <TooltipLine>{`${formatCount(batch.rowCount)} rows`}</TooltipLine>
    {/* Only failed batches carry a message, and it is the whole reason an
        operator hovers a red cell. */}
    {batch.errorMessage !== null && (
      <TooltipLine>{batch.errorMessage}</TooltipLine>
    )}
  </HHStack>
);

export interface BatchHeatmapProps {
  batches: BatchHeatmapCell[];
}

/**
 * Byte-range chunking state for one file: a grid of one cell per parsing
 * batch, ordered by byte offset, so the shape of a failure (a single bad
 * chunk vs. everything after a point) is visible at a glance.
 */
export const BatchHeatmap = ({ batches }: BatchHeatmapProps): JSX.Element => {
  // The PROCESSING pulse runs for as long as a batch is in flight, which can
  // exceed the 5s threshold at which WCAG 2.2.2 requires a pause/stop/hide
  // control. Honouring the OS-level reduced-motion preference is that
  // control: it lets an operator who cannot tolerate persistent motion opt
  // out globally, without adding a bespoke per-cell toggle for a purely
  // decorative status cue (the colour itself already conveys status).
  const prefersReducedMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)',
    { noSsr: true },
  );

  if (batches.length === 0) {
    return (
      <HHBox data-testid={BATCH_HEATMAP_EMPTY_TEST_ID} paddingY={2}>
        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="text.secondary"
        >
          No batches have been created for this ingestion yet.
        </HHTypography>
      </HHBox>
    );
  }

  return (
    <HHBox
      data-testid={BATCH_HEATMAP_TEST_ID}
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${CELL_SIZE_PX}px, 1fr))`,
        gap: 0.5,
      }}
    >
      {sortBatchesByByteOffset(batches).map(batch => (
        <HHTooltip
          key={batch.batchId}
          hhVariant="variant-bypass"
          title={<BatchTooltip batch={batch} />}
        >
          {/* role="img" + aria-label: the cell's only visual content is its
              colour, which a screen reader cannot convey. */}
          <HHBox
            role="img"
            tabIndex={0}
            aria-label={`Batch ${batch.batchId}: ${
              FILE_PARSING_BATCH_STATUS_LABELS[batch.status]
            }`}
            data-testid={batchHeatmapCellTestId(batch.batchId)}
            data-status={batch.status}
            sx={{
              height: CELL_SIZE_PX,
              borderRadius: '2px',
              backgroundColor: BATCH_CELL_COLORS[batch.status],
              cursor: 'pointer',
              animation: shouldPulseCell(batch.status, prefersReducedMotion)
                ? `${PULSE_KEYFRAMES} 1.5s ease-in-out infinite`
                : undefined,
              [`@keyframes ${PULSE_KEYFRAMES}`]: {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }}
          />
        </HHTooltip>
      ))}
    </HHBox>
  );
};
